import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api';
import { useAuth } from '../context/AuthContext';

export default function LoginPage() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setIsPending(true);
    try {
      await login(email, password);
      const redirect = params.get('redirect');
      navigate(redirect ? decodeURIComponent(redirect) : '/dashboard');
    } catch (err) {
      if (err instanceof ApiError) {
        setError(t([`error.${err.message}`, 'error.LOGIN_FAILED']));
      } else {
        setError(t('error.UNKNOWN'));
      }
      setIsPending(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <form
        onSubmit={handleSubmit}
        className="max-w-sm w-full bg-white rounded-lg shadow p-8 space-y-4"
      >
        <h1 className="text-2xl font-bold text-slate-900">{t('login.title')}</h1>

        {error && <div className="rounded bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>}

        <label className="block">
          <span className="text-sm font-medium text-slate-700">{t('field.email')}</span>
          <input
            type="email"
            required
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 block w-full rounded border border-slate-300 px-3 py-2 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-slate-700">{t('field.password')}</span>
          <input
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1 block w-full rounded border border-slate-300 px-3 py-2 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
          />
        </label>

        <button
          type="submit"
          disabled={isPending}
          className="w-full bg-slate-900 text-white rounded px-4 py-2 hover:bg-slate-800 disabled:opacity-50"
        >
          {isPending ? t('common.working') : t('login.submit')}
        </button>

        <div className="flex justify-between text-sm">
          <Link to="/forgot-password" className="text-slate-600 hover:underline">
            {t('login.forgotPassword')}
          </Link>
          <Link to="/signup" className="text-slate-600 hover:underline">
            {t('login.noAccount')}
          </Link>
        </div>
      </form>
    </div>
  );
}
